import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtId } from '../utils/jiraId';
import { recordLabel } from '../utils/recordId';
import { entityPath, ENTITY_PREFIX } from '../utils/entityRoutes';
import { currencySymbol } from '../utils/currency';
import '../styles/components/FabricCatalog.css';
import Icon from './Icon';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import LineItemsEditor from './ui/LineItemsEditor';
import ContentCard from './ContentCard';
import RichTextEditor from './ui/RichTextEditor';
import SidebarPhotoCard from './ui/SidebarPhotoCard';
import RecordActivity from './ui/RecordActivity';
import SearchableSelect from './ui/SearchableSelect';
import { Fabric, AsoebiFilter, StockMovementReason, STOCK_REASON_LABELS } from '../types/ui/Asoebi';
import { UnifiedBundle, UnifiedBundleItem } from '../types/Product';
import { createFabric, updateFabric, archiveFabric, restoreFabric, loadAssignments } from '../services/AsoebiService';
import { uploadImage, getAttachments, deleteAttachment, Attachment, resolveMediaUrl } from '../services/uploadService';
import { loadBundles, createBundle, updateBundle, deleteBundle, refreshBundlesFromApi } from '../services/BundleService';
import { useNotifications } from './NotificationProvider';
import { refersToFabric } from '../utils/fabricRef';
import { useMoney } from '../hooks/useMoney';
import RecordListPublisher from './RecordListPublisher';

interface FabricCatalogProps {
  fabrics: Fabric[];
  onUpdate: (fabric: Fabric) => void;
  onDelete: (fabricId: string) => void;
  filter: AsoebiFilter;
  onFilterChange: (filter: AsoebiFilter) => void;
  fabricId?: string;
}

const FabricCatalog: React.FC<FabricCatalogProps> = ({
  fabrics,
  onUpdate,
  onDelete,
  filter,
  onFilterChange,
  fabricId,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { format: formatMoney, symbol: currencySymbolDisplay, unit: currencyUnit } = useMoney();
  const styleGuideInputRef = useRef<HTMLInputElement>(null);
  const bundleImgInputRef = useRef<HTMLInputElement>(null);
  const editingFabricRef = useRef<Fabric | null>(null);
  const [imageUploading, setImageUploading] = useState(false);

  // ── Attachments (covers all labelled photos for this fabric) ───────────────
  const [galleryAttachments, setGalleryAttachments] = useState<Attachment[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [styleGuideUploading, setStyleGuideUploading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFabric, setEditingFabric] = useState<Fabric | null>(null);
  const [stockQty, setStockQty] = useState(0);
  const [stockReason, setStockReason] = useState<StockMovementReason>('received');
  const [stockNote, setStockNote] = useState('');
  const [newFabric, setNewFabric] = useState({
    name: '',
    color: '',
    price: 0,
    stock: 0,
    image: '',
    category: 'other' as Fabric['category'],
    description: '',
    is_active: true,
    published_to_shop: false,
  });

  // ── Bundle state ────────────────────────────────────────────────────────
  const [bundles, setBundles] = useState<UnifiedBundle[]>(() => loadBundles());
  const [showBundleForm, setShowBundleForm] = useState(false);

  useEffect(() => {
    // A failed refresh leaves `bundles` at whatever it already held (the
    // picker and the bundle cards just show stale data) rather than
    // throwing out of this effect — background refresh failures never toast.
    refreshBundlesFromApi().then(setBundles).catch(() => {});
  }, []);
  const [editingBundle, setEditingBundle] = useState<UnifiedBundle | null>(null);
  const [fabricConfirm, setFabricConfirm] = useState<{ type: 'delete-bundle' | 'archive-fabric'; id: string } | null>(null);
  const [bundleDraft, setBundleDraft] = useState<{
    name: string;
    description: string;
    price: string;
    image: string;
    items: Array<{ productId: string; yards: string }>;
  }>({ name: '', description: '', price: '', image: '', items: [] });

  const openNewBundle = () => {
    setEditingBundle(null);
    setBundleDraft({ name: '', description: '', price: '', image: '', items: [{ productId: '', yards: '' }] });
    setShowBundleForm(true);
  };

  const closeBundleForm = () => {
    setShowBundleForm(false);
    setEditingBundle(null);
  };

  const saveBundleDraft = async () => {
    const name = bundleDraft.name.trim();
    const price = parseFloat(bundleDraft.price) || 0;
    if (!name) { showError('Bundle name is required.'); return; }
    const items: UnifiedBundleItem[] = bundleDraft.items
      .filter(i => i.productId && parseFloat(i.yards) > 0)
      .map(i => {
        const fab = fabrics.find(f => f.id === i.productId);
        return {
          productId: i.productId,
          productName: fab?.name ?? '',
          quantity: parseFloat(i.yards),
          unitLabel: 'yds',
        };
      });
    if (items.length === 0) { showError('Add at least one fabric item to the bundle.'); return; }

    const image = bundleDraft.image.trim() || undefined;
    if (editingBundle) {
      let updated;
      try {
        updated = await updateBundle(editingBundle.id, {
          name, description: bundleDraft.description.trim() || undefined, price, image, items,
          types: editingBundle.types.length > 0 ? editingBundle.types : ['asoebi'],
          priceMode: 'manual',
          is_active: editingBundle.is_active,
        });
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to save bundle.');
        return;
      }
      // A null here means the bundle left the cache mid-edit; it used to close
      // the form silently as though the save had gone through.
      if (!updated) { showError('That bundle is no longer listed — refresh and try again.'); return; }
      setBundles(loadBundles());
      showSuccess(`Bundle "${updated.name}" saved.`);
      closeBundleForm();
    } else {
      let created;
      try {
        created = await createBundle({
          name, description: bundleDraft.description.trim() || undefined,
          price, image, items,
          types: ['asoebi'],
          priceMode: 'manual',
          is_active: true,
        });
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to create bundle.');
        return;
      }
      setBundles(loadBundles());
      showSuccess(`Bundle "${created.name}" created.`);
      closeBundleForm();
    }
  };

  const handleAddFabric = async () => {
    if (!newFabric.name.trim()) return;

    let fabric: Fabric;
    try {
      fabric = await createFabric({ ...newFabric, price: String(newFabric.price) });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add fabric.');
      return;
    }
    onUpdate(fabric);
    showSuccess(`Fabric "${fabric.name}" added.`, { persistent: true });

    setNewFabric({
      name: '',
      color: '',
      price: 0,
      stock: 0,
      image: '',
      category: 'other',
      description: '',
      is_active: true,
      published_to_shop: false,
    });
    setShowAddModal(false);
  };

  const handleSaveEdit = async () => {
    if (!editingFabric || !editingFabric.name.trim()) return;

    try {
      const updated = await updateFabric(editingFabric.id, editingFabric);
      if (updated) {
        onUpdate(updated);
        showSuccess('Fabric saved.');
      } else {
        showError('Failed to save fabric.');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save fabric.');
    }
  };

  // The server (db/repositories/planning/asoebi/fabrics.ts) logs every stock
  // change to the stock_movements ledger automatically on update — this just
  // sends the new total; reason/note are kept locally for the Record button's
  // own label but have no server field to land in yet (no per-fabric history
  // GET route on port/kal), so the history list below was dropped with it.
  const handleAddStockMovement = async (fabric: Fabric) => {
    if (stockQty === 0) return;
    const newStock = Math.max(0, fabric.stock + stockQty);
    let updated: Fabric;
    try {
      updated = await updateFabric(fabric.id, { stock: newStock });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to record stock movement.');
      return;
    }
    onUpdate(updated);
    setEditingFabric(prev => (prev ? { ...prev, stock: updated.stock } : updated));
    setStockQty(0);
    setStockNote('');
    setStockReason('received');
  };

  const handleDeleteFabric = (id: string) => {
    setFabricConfirm({ type: 'archive-fabric', id });
  };

  // Initialize editing state when viewing a fabric
  useEffect(() => {
    if (!fabricId) return;
    const fabric = fabrics.find(f => f.id === fabricId);
    if (fabric && !editingFabric) {
      setEditingFabric({ ...fabric });
    }
  }, [fabricId, fabrics]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load gallery attachments whenever we open a fabric detail
  useEffect(() => {
    if (!fabricId) { setGalleryAttachments([]); return; }
    setGalleryLoading(true);
    getAttachments('fabric', fabricId)
      .then(atts => setGalleryAttachments(atts))
      .catch(() => setGalleryAttachments([]))
      .finally(() => setGalleryLoading(false));
  }, [fabricId]);

  // Keep ref in sync so event listeners always read the latest editing state
  // without needing to re-register on every object reference change.
  useEffect(() => {
    editingFabricRef.current = editingFabric;
  }); // intentionally no deps — runs after every render

  // Listen for header breadcrumb actions.
  // Depends only on fabricId so there is never more than one listener pair
  // active at a time (fixes duplicate-confirm bug when editingFabric object
  // reference changes mid-render).
  useEffect(() => {
    if (!fabricId) return;
    const onSave = async () => {
      const ef = editingFabricRef.current;
      if (!ef || !ef.name.trim()) return;
      try {
        const updated = await updateFabric(ef.id, ef);
        if (updated) { onUpdate(updated); showSuccess('Fabric saved.'); }
        else { showError('Failed to save fabric.'); }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to save fabric.');
      }
    };
    const onDel = () => {
      const ef = editingFabricRef.current;
      if (ef) handleDeleteFabric(ef.id);
    };
    // Detail-page counterpart of handleRestoreFabric below (that one only
    // exists for the archived-list view's per-row button, defined after this
    // component's early return for the detail page) — same restoreFabric
    // call, triggered by the breadcrumb's Restore button instead.
    const onRestore = async () => {
      const ef = editingFabricRef.current;
      if (!ef) return;
      try {
        const restored = await restoreFabric(ef.id);
        if (restored) { onUpdate(restored); showSuccess(`"${restored.name}" restored.`); }
        else { showError('Failed to restore fabric.'); }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to restore fabric.');
      }
    };
    window.addEventListener('fabric-save', onSave);
    window.addEventListener('fabric-delete', onDel);
    window.addEventListener('fabric-restore', onRestore);
    return () => {
      window.removeEventListener('fabric-save', onSave);
      window.removeEventListener('fabric-delete', onDel);
      window.removeEventListener('fabric-restore', onRestore);
    };
  }, [fabricId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync fabric is_active status to/from breadcrumb toggle buttons
  useEffect(() => {
    if (!fabricId || !editingFabric) return;
    const status = editingFabric.is_active ? 'active' : 'inactive';
    window.dispatchEvent(new CustomEvent('fabric-status-sync', { detail: status }));
  }, [fabricId, editingFabric?.is_active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fabricId) return;
    const onStatusSet = (e: Event) => {
      setEditingFabric(prev => prev ? { ...prev, is_active: (e as CustomEvent).detail as boolean } : prev);
    };
    window.addEventListener('fabric-status-set', onStatusSet);
    return () => window.removeEventListener('fabric-status-set', onStatusSet);
  }, [fabricId]);

  // Same sync pattern for published_to_shop, now a breadcrumb toggle instead
  // of a checkbox buried in the form body.
  useEffect(() => {
    if (!fabricId || !editingFabric) return;
    window.dispatchEvent(new CustomEvent('fabric-published-sync', { detail: !!editingFabric.published_to_shop }));
  }, [fabricId, editingFabric?.published_to_shop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same sync pattern for archived — without it the breadcrumb's Archive
  // button never learns the fabric was actually archived (e.g. by the
  // confirm-dialog flow this same page uses), so it kept showing "Archive"
  // forever instead of switching to "Restore" the way every other detail
  // page with an archive/restore toggle already does.
  useEffect(() => {
    if (!fabricId || !editingFabric) return;
    window.dispatchEvent(new CustomEvent('fabric-archived-sync', { detail: !!editingFabric.archived }));
  }, [fabricId, editingFabric?.archived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fabricId) return;
    const onPublishedSet = (e: Event) => {
      setEditingFabric(prev => prev ? { ...prev, published_to_shop: (e as CustomEvent).detail as boolean } : prev);
    };
    window.addEventListener('fabric-published-set', onPublishedSet);
    return () => window.removeEventListener('fabric-published-set', onPublishedSet);
  }, [fabricId]);

  // ── Fabric Detail Page ──────────────────────────────────────────────────
  if (fabricId) {
    const fabric = fabrics.find(f => f.id === fabricId);

    if (!fabric) {
      return (
        <EmptyState
          icon="list"
          title="Fabric not found"
          description="The fabric you are looking for does not exist or has been removed."
          action={<button className="primary-btn" onClick={() => navigate('/asoebi')}>Back to Asoebi</button>}
        />
      );
    }

    if (!editingFabric) return null;

    // refersToFabric, not a bare id match: fabric and bundle ids share one
    // numeric space, so an id-only filter counted bundle assignments as this
    // fabric's own reserved/paid yards and revenue.
    const fabricAssignments = loadAssignments().filter(a => refersToFabric(a, fabric.id, bundles) && !a.archived);
    const reservedYards = fabricAssignments
      .filter(a => a.status === 'pending_confirmation' || a.status === 'ordered')
      .reduce((s, a) => s + (Number(a.yards) || 0), 0);
    const paidYards = fabricAssignments
      .filter(a => a.status === 'paid' || a.status === 'delivered' || a.status === 'collected')
      .reduce((s, a) => s + (Number(a.yards) || 0), 0);
    const revenue = fabricAssignments
      .filter(a => a.status === 'paid' || a.status === 'delivered' || a.status === 'collected')
      .reduce((s, a) => s + (Number(a.total_price) || 0), 0);
    const costPerYard = (fabric as any).costPerYard as number | undefined;
    const totalCost = costPerYard != null ? costPerYard * paidYards : null;
    const margin = totalCost != null && revenue > 0 ? ((revenue - totalCost) / revenue) * 100 : null;
    const fabricBundles = bundles.filter(b => b.items.some(i => i.productId === fabric.id));

    const coverUrl = editingFabric.image
      ? resolveMediaUrl(editingFabric.image)
      : null;

    const handleCoverImageFile = async (file: File) => {
      if (!editingFabric) return;
      setImageUploading(true);
      try {
        const att = await uploadImage(file, { entityType: 'fabric', entityId: String(editingFabric.id), label: editingFabric.name });
        setEditingFabric({ ...editingFabric, image: resolveMediaUrl(att.url) });
      } catch { showError('Image upload failed'); }
      setImageUploading(false);
    };

    return (
      <>
      <div className="fc-detail-root">
      <div className="detail-layout">
        <div className="inline-form-page">
          <div className="form-row">
            <div className="form-group">
              <label>Name <span className="form-required">*</span></label>
              <input
                type="text"
                value={editingFabric.name}
                onChange={(e) => setEditingFabric({ ...editingFabric, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <SearchableSelect
                value={editingFabric.category}
                onChange={(value) => setEditingFabric({ ...editingFabric, category: value as Fabric['category'] })}
                options={[
                  { value: 'lace', label: 'Lace' },
                  { value: 'ankara', label: 'Ankara' },
                  { value: 'velvet', label: 'Velvet' },
                  { value: 'silk', label: 'Silk' },
                  { value: 'organza', label: 'Organza' },
                  { value: 'adire', label: 'Adire' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Colour</label>
              <div className="fc-colour-field">
                <input
                  type="color"
                  className="fc-colour-picker"
                  value={/^#[0-9a-fA-F]{6}$/.test(editingFabric.color ?? '') ? (editingFabric.color as string) : '#8b5a3c'}
                  onChange={e => setEditingFabric({ ...editingFabric, color: e.target.value })}
                  title="Pick a colour"
                />
                <input
                  type="text"
                  className="fc-colour-text"
                  value={editingFabric.color ?? ''}
                  onChange={(e) => setEditingFabric({ ...editingFabric, color: e.target.value })}
                  placeholder="e.g. Ivory, #f5f0e8"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Price per Yard{editingFabric.currency ? ` (${editingFabric.currency})` : ''}</label>
              <input
                type="text"
                inputMode="decimal"
                value={editingFabric.price}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, '');
                  setEditingFabric({ ...editingFabric, price: v });
                }}
              />
            </div>
          </div>

          <RichTextEditor
            label="Description"
            value={editingFabric.description ?? ''}
            onChange={v => setEditingFabric({ ...editingFabric, description: v })}
            rows={3}
          />

          {editingFabric.published_to_shop && Number(editingFabric.price) <= 0 && (
            <div className="form-group">
              <p className="form-hint fc-price-warning">
                No price set — guests will see this as free or unbuyable. Set a price before publishing (use the Published toggle in the breadcrumb above).
              </p>
            </div>
          )}

          {/* ── Style Guide ─────────────────────────────── */}
          <div className="form-section">
            <div className="fc-sg-header">
              <div>
                <p className="form-section-title">Style Guide</p>
                <p className="form-hint">How to wear and style this fabric — outfit references, colour pairings, mood boards.</p>
              </div>
              <button
                className="fc-sg-add-btn"
                onClick={() => styleGuideInputRef.current?.click()}
                disabled={styleGuideUploading}
              >
                <Icon name="plus" size={13} />
                {styleGuideUploading ? 'Uploading…' : 'Add images'}
              </button>
            </div>

            {galleryLoading ? (
              <p className="fc-gallery-loading">Loading…</p>
            ) : (() => {
              const sgImages = galleryAttachments.filter(a => a.label === 'style_guide');
              return sgImages.length === 0 ? (
                <button
                  className="fc-sg-empty"
                  onClick={() => styleGuideInputRef.current?.click()}
                  disabled={styleGuideUploading}
                >
                  <Icon name="image" size={18} />
                  <span>No style guide images yet — click to upload</span>
                </button>
              ) : (
                <div className="fc-sg-grid">
                  {sgImages.map(att => {
                    const fullUrl = resolveMediaUrl(att.url);
                    return (
                      <div key={att.id} className="fc-sg-thumb">
                        <img src={fullUrl} alt="Style guide" className="fc-sg-img" />
                        <button
                          className="fc-sg-delete"
                          title="Remove"
                          onClick={async () => {
                            try {
                              await deleteAttachment(att.id);
                              setGalleryAttachments(prev => prev.filter(a => a.id !== att.id));
                            } catch { showError('Could not remove image'); }
                          }}
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    className="fc-sg-add-tile"
                    onClick={() => styleGuideInputRef.current?.click()}
                    disabled={styleGuideUploading}
                    title="Add more"
                  >
                    <Icon name="plus" size={18} />
                  </button>
                </div>
              );
            })()}

            <input
              ref={styleGuideInputRef}
              type="file"
              accept="image/*"
              multiple
              className="u-hidden"
              onChange={async e => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (!files.length || !fabricId) return;
                setStyleGuideUploading(true);
                try {
                  const uploaded = await Promise.all(
                    files.map(f => uploadImage(f, {
                      entityType: 'fabric',
                      entityId: fabricId,
                      label: 'style_guide',
                    }))
                  );
                  setGalleryAttachments(prev => [...prev, ...uploaded]);
                } catch { showError('Upload failed — please try again'); }
                finally { setStyleGuideUploading(false); }
              }}
            />
          </div>

          {/* Stock Update */}
          <div className="fc-stock-section">
            <h4 className="fc-stock-title">Update Stock</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  value={stockQty}
                  onChange={(e) => setStockQty(parseInt(e.target.value) || 0)}
                  placeholder="+50 or -10"
                />
              </div>
              <div className="form-group">
                <label>Reason</label>
                <SearchableSelect
                  value={stockReason}
                  onChange={(value) => setStockReason(value as StockMovementReason)}
                  options={(Object.keys(STOCK_REASON_LABELS) as StockMovementReason[]).map(r => ({ value: r, label: STOCK_REASON_LABELS[r] }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group fc-note-group">
                <label>Note (optional)</label>
                <input
                  type="text"
                  value={stockNote}
                  onChange={(e) => setStockNote(e.target.value)}
                  placeholder="e.g. Batch from Lagos supplier"
                />
              </div>
              <button
                className="btn-primary fc-record-btn"
                onClick={() => handleAddStockMovement(fabric)}
                disabled={stockQty === 0}
              >
                Record
              </button>
            </div>
          </div>
        </div>{/* inline-form-page */}

        <aside className="detail-sidebar">
          {/* ── Cover photo ───────────────────────────────── */}
          <SidebarPhotoCard
            url={coverUrl || undefined}
            alt={editingFabric?.name || 'Cover'}
            uploading={imageUploading}
            onFileSelect={handleCoverImageFile}
            onRemove={() => editingFabric && setEditingFabric({ ...editingFabric, image: '' })}
          />

          <section className="side-section">
            <h4>Stock</h4>
            <div className="stat-row"><span>In stock</span><strong>{fabric.stock} yds</strong></div>
            <div className="stat-row"><span>Reserved</span><strong>{reservedYards} yds</strong></div>
            <div className="stat-row"><span>Paid for</span><strong>{paidYards} yds</strong></div>
          </section>

          <section className="side-section">
            <h4>Financials</h4>
            <div className="stat-row"><span>Price / yd</span><strong>{formatMoney(Number(fabric.price), fabric.currency)}</strong></div>
            <div className="stat-row"><span>Cost / yd</span><strong>{costPerYard != null ? formatMoney(costPerYard, fabric.currency) : '—'}</strong></div>
            <div className="stat-row"><span>Revenue (paid)</span><strong>{formatMoney(revenue, fabric.currency)}</strong></div>
            <div className="stat-row"><span>Total cost</span><strong>{totalCost != null ? formatMoney(totalCost, fabric.currency) : '—'}</strong></div>
            <div className="stat-row"><span>Margin</span><strong>{margin != null ? `${margin.toFixed(1)}%` : '—'}</strong></div>
          </section>

          <section className="side-section fc-bundles-section">
            <h4>Packages &amp; bundles</h4>

            {fabricBundles.length === 0 && !showBundleForm && (
              <p className="fc-side-empty">Not in any bundle yet. Go to Bundles to create one.</p>
            )}

            {fabricBundles.map(bundle => (
              <button
                key={bundle.id}
                type="button"
                className="fc-bundle-card fc-bundle-card-link"
                onClick={() => navigate('/asoebi', { state: { asoebiTab: 'bundles' } })}
                title="View in Bundles"
              >
                {bundle.image && (
                  <img src={bundle.image} alt={bundle.name} className="fc-bundle-card-img" />
                )}
                <div className="fc-bundle-header">
                  <span className="fc-bundle-name">{bundle.name}</span>
                  <Icon name="chevronRight" size={14} />
                </div>
                {bundle.description && <p className="fc-bundle-desc">{bundle.description}</p>}
                <ul className="fc-bundle-items">
                  {bundle.items.map((item, i) => {
                    const fab = fabrics.find(f => f.id === item.productId);
                    return (
                      <li key={i}>
                        <span>{fab ? fab.name : item.productName || <em className="fc-bundle-missing">Unknown fabric</em>}</span>
                        <span className="fc-bundle-yards">{item.quantity} yds</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="fc-bundle-price">{formatMoney(bundle.price)}</div>
              </button>
            ))}
            {showBundleForm && (
              <div className="fc-bundle-form">
                <div className="form-group">
                  <label>Bundle name <span className="form-required">*</span></label>
                  <input
                    type="text"
                    value={bundleDraft.name}
                    onChange={e => setBundleDraft(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Aso-ebi Full Set"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={bundleDraft.description}
                    onChange={e => setBundleDraft(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional note"
                  />
                </div>

                {/* Bundle image */}
                <div className="form-group">
                  <label>Package photo</label>
                  {bundleDraft.image ? (
                    <div className="fc-bundle-img-preview">
                      <img src={bundleDraft.image} alt="Bundle" className="fc-bundle-img" />
                      <div className="fc-bundle-img-actions">
                        <button className="fc-photo-btn" onClick={() => bundleImgInputRef.current?.click()}>
                          <Icon name="camera" size={12} /> Replace
                        </button>
                        <button className="fc-photo-btn fc-photo-btn-danger" onClick={() => setBundleDraft(prev => ({ ...prev, image: '' }))}>
                          <Icon name="trash" size={12} /> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className="fc-bundle-img-empty" onClick={() => bundleImgInputRef.current?.click()}>
                      <Icon name="image" size={16} />
                      <span>Upload package photo</span>
                    </button>
                  )}
                  <input
                    ref={bundleImgInputRef}
                    type="file"
                    accept="image/*"
                    className="u-hidden"
                    onChange={async e => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f || !fabricId) return;
                      try {
                        const att = await uploadImage(f, { entityType: 'bundle', entityId: editingBundle?.id ?? `bundle-new-${fabricId}`, label: 'cover' });
                        setBundleDraft(prev => ({ ...prev, image: resolveMediaUrl(att.url) }));
                      } catch { showError('Image upload failed'); }
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Bundle price{currencySymbolDisplay ? ` (${currencySymbolDisplay})` : ` (${currencyUnit})`} <span className="form-required">*</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={Number(bundleDraft.price) > 0 ? Number(bundleDraft.price).toLocaleString() : ''}
                    onChange={e => setBundleDraft(prev => ({ ...prev, price: String(Number(e.target.value.replace(/,/g, '')) || 0) }))}
                    placeholder="0"
                  />
                </div>

                <label className="fc-bundle-items-label">Fabrics in bundle</label>
                <LineItemsEditor
                  items={bundleDraft.items.map((item, idx) => ({ id: String(idx), values: { productId: item.productId, yards: item.yards } }))}
                  fields={[
                    { key: 'productId', type: 'picker', placeholder: 'Pick fabric…', options: fabrics.filter(f => f.is_active).map(f => ({ value: f.id, label: f.name })) },
                    { key: 'yards', type: 'number', unit: 'yds', step: '0.5' },
                  ]}
                  onChange={rows => setBundleDraft(prev => ({
                    ...prev,
                    items: rows.map(r => ({ productId: r.values.productId, yards: r.values.yards })),
                  }))}
                  addLabel="Add fabric"
                />

                <div className="fc-bundle-form-actions">
                  <button className="secondary-btn" onClick={closeBundleForm}>Cancel</button>
                  <button className="primary-btn" onClick={saveBundleDraft}>
                    {editingBundle ? 'Save' : 'Create'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </aside>
      </div>
      {/* A fabric is a `products` row (types @> ['fabric']) — see gg-main's
          db/repositories/planning/asoebi.ts — so its thread is the product
          entity's, not a fabric-specific one. Comments only: fabrics carry
          active/archived flags, not a status worth trailing. */}
      <RecordActivity entityType="product" entityId={String(editingFabric.id)} />
      </div>
      <ConfirmDialog
        open={fabricConfirm !== null}
        onClose={() => setFabricConfirm(null)}
        onConfirm={async () => {
          if (!fabricConfirm) return;
          const activeAssignments = loadAssignments().filter(
            a => refersToFabric(a, fabricConfirm.id, bundles) && !a.archived
          );
          if (activeAssignments.length > 0) {
            showError(`Cannot archive: ${activeAssignments.length} active assignment${activeAssignments.length !== 1 ? 's' : ''} use this fabric.`);
            setFabricConfirm(null);
            return;
          }
          const archivedName = fabrics.find(f => f.id === fabricConfirm.id)?.name ?? 'Fabric';
          try {
            await archiveFabric(fabricConfirm.id);
            onDelete(fabricConfirm.id);
            showSuccess(`"${archivedName}" archived.`);
            navigate('/asoebi/fabrics');
          } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to archive fabric.');
          }
          setFabricConfirm(null);
        }}
        title={`Archive "${fabrics.find(f => f.id === fabricConfirm?.id)?.name || 'this fabric'}"?`}
        description="It will be hidden but not permanently deleted."
        confirmLabel="Archive"
      />
      </>
    );
  }

  // ── Fabric Grid (list view) ─────────────────────────────────────────────
  const isArchivedView = !!filter.archived;

  const handleRestoreFabric = async (fabricToRestore: Fabric) => {
    try {
      const restored = await restoreFabric(fabricToRestore.id);
      if (restored) {
        onUpdate(restored);
        showSuccess(`"${restored.name}" restored.`);
      } else {
        showError('Failed to restore fabric.');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to restore fabric.');
    }
  };

  return (
    <>
      {/* Fabric Grid */}
      {!isArchivedView && <RecordListPublisher kind="fabric" ids={fabrics.map(f => f.id)} />}
      <div className="fabric-grid">
        {fabrics.length === 0 ? (
          isArchivedView ? (
            <EmptyState
              icon="folder"
              title="No archived fabrics"
              description="Fabrics you archive will show up here, with a way back."
            />
          ) : (
            <EmptyState
              icon="list"
              title="No fabrics found"
              description="Add your first fabric to get started"
              action={<button className="primary-btn" onClick={() => navigate('/fabric/new')}>New Fabric</button>}
            />
          )
        ) : (
          fabrics.map(fabric => (
            <ContentCard
              key={fabric.id}
              className={!fabric.is_active ? 'inactive' : ''}
              onClick={() => isArchivedView ? undefined : navigate(entityPath('fabric', fabric.id) ?? '/asoebi/fabrics')}
              image={fabric.image ?? undefined}
              imagePlaceholder={<Icon name="image" size={32} />}
              title={fabric.name}
              status={{
                label: fabric.is_active ? 'Active' : 'Inactive',
                type: fabric.is_active ? 'active' : 'inactive'
              }}
              tags={undefined}
              badges={[
                ...(fabric.stock === 0 ? [{ label: 'Out of Stock', type: 'danger' as const }] : []),
                ...(fabric.stock < 5 && fabric.stock > 0 ? [{ label: 'Low Stock', type: 'warning' as const }] : [])
              ]}
              details={[
                { label: 'ID', value: recordLabel('fabric', fabric.id) },
                { label: 'Color', value: fabric.color ?? '—', swatch: fabric.color || undefined }
              ]}
              pricing={{
                amount: Number(fabric.price),
                currency: currencySymbol(fabric.currency),
                unit: 'yard',
                stock: {
                  amount: fabric.stock,
                  status: fabric.stock === 0 ? 'out' : fabric.stock < 5 ? 'low' : 'normal',
                  label: `${fabric.stock} yards left`
                }
              }}
              description={fabric.description ?? undefined}
              actions={isArchivedView ? [{
                icon: 'refresh',
                title: 'Restore',
                onClick: () => { void handleRestoreFabric(fabric); },
              }] : undefined}
            />
          ))
        )}
      </div>

      {/* Add Fabric Modal */}
      {showAddModal && (
        <div className="inline-form-page">
          <div className="inline-form-header">
            <button className="btn-back" onClick={() => setShowAddModal(false)}>
              <Icon name="arrowLeft" size={16} /> Back
            </button>
            <div className="flex items-center gap-2">
              <h3>Add New Fabric</h3>
              <button className="btn-primary" onClick={handleAddFabric}>New Fabric</button>
            </div>
          </div>

          <div className="inline-form-page">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name <span className="form-required">*</span></label>
                  <input
                    type="text"
                    value={newFabric.name}
                    onChange={(e) => setNewFabric({ ...newFabric, name: e.target.value })}
                    placeholder="e.g., Royal Blue Lace"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <SearchableSelect
                    value={newFabric.category}
                    onChange={(value) => setNewFabric({ ...newFabric, category: value as Fabric['category'] })}
                    options={[
                      { value: 'lace', label: 'Lace' },
                      { value: 'ankara', label: 'Ankara' },
                      { value: 'velvet', label: 'Velvet' },
                      { value: 'silk', label: 'Silk' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={newFabric.color}
                    onChange={(e) => setNewFabric({ ...newFabric, color: e.target.value })}
                    placeholder="e.g., Navy Blue"
                  />
                </div>

                <div className="form-group">
                  <label>Price per Yard{currencySymbolDisplay ? ` (${currencySymbolDisplay})` : ` (${currencyUnit})`}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newFabric.price > 0 ? newFabric.price.toLocaleString() : ''}
                    onChange={(e) => setNewFabric({ ...newFabric, price: parseFloat(e.target.value.replace(/,/g, '')) || 0 })}
                  />
                </div>

                <div className="form-group">
                  <label>Stock (yards)</label>
                  <input
                    type="number"
                    value={newFabric.stock}
                    onChange={(e) => setNewFabric({ ...newFabric, stock: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>

                <RichTextEditor
                  className="full-width"
                  label="Description"
                  value={newFabric.description}
                  onChange={v => setNewFabric({ ...newFabric, description: v })}
                  placeholder="Describe the fabric..."
                  rows={3}
                />
              </div>

          </div>
        </div>
      )}
      <ConfirmDialog
        open={fabricConfirm !== null}
        onClose={() => setFabricConfirm(null)}
        onConfirm={async () => {
          if (!fabricConfirm) return;
          if (fabricConfirm.type === 'delete-bundle') {
            try {
              await deleteBundle(fabricConfirm.id);
            } catch (err) {
              showError(err instanceof Error ? err.message : 'Failed to delete bundle.');
              return;
            }
            setBundles(loadBundles());
            showSuccess('Bundle deleted.');
          } else {
            const activeAssignments = loadAssignments().filter(
              a => refersToFabric(a, fabricConfirm.id, bundles) && !a.archived
            );
            if (activeAssignments.length > 0) {
              showError(`Cannot archive: ${activeAssignments.length} active assignment${activeAssignments.length !== 1 ? 's' : ''} use this fabric.`);
              setFabricConfirm(null);
              return;
            }
            const archivedName = fabrics.find(f => f.id === fabricConfirm.id)?.name ?? 'Fabric';
            try {
              await archiveFabric(fabricConfirm.id);
              onDelete(fabricConfirm.id);
              showSuccess(`"${archivedName}" archived.`);
              navigate('/asoebi/fabrics');
            } catch (err) {
              showError(err instanceof Error ? err.message : 'Failed to archive fabric.');
            }
          }
          setFabricConfirm(null);
        }}
        title={fabricConfirm?.type === 'delete-bundle'
          ? `Delete bundle "${bundles.find(b => b.id === fabricConfirm?.id)?.name}"?`
          : `Archive "${fabrics.find(f => f.id === fabricConfirm?.id)?.name || 'this fabric'}"?`}
        description={fabricConfirm?.type === 'delete-bundle'
          ? 'This cannot be undone.'
          : 'It will be hidden but not permanently deleted.'}
        confirmLabel={fabricConfirm?.type === 'archive-fabric' ? 'Archive' : 'Delete'}
      />
    </>
  );
};

export default FabricCatalog;
